import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { clientApis } from "../../../Data/axios";
import config from "../../../Data/AuthUser";
import * as XLSX from "xlsx";

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Status configuration mapping
const statusConfig = {
  "1": { icon: "✓", color: "#4caf50", label: "Present", bgColor: "#e8f5e9" },
  "1603": { icon: "✕", color: "#f44336", label: "Absent", bgColor: "#ffebee" },
  "1610": { icon: "🍃", color: "#333", label: "Leave Request", bgColor: "#f5f5f5" },
  "1612": { icon: "✈", color: "#9c27b0", label: "On Leave", bgColor: "#f3e5f5" },
  "1605": { icon: "🏠", color: "orange", label: "Relieved", bgColor: "#fff3e0" },
  "1_no_loc": { icon: "⌨️", color: "#ff9800", label: "Present (No Location)", bgColor: "#ffe0b2" },
};

const getDaysInMonth = (month, year) => {
  return new Date(year, month + 1, 0).getDate();
};

const getDayName = (date, month, year) => {
  const dayIndex = new Date(year, month, date).getDay();
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  return days[dayIndex];
};

const formatDateString = (monthValue, yearValue, day) => {
  return `${yearValue}-${String(monthValue + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

// Convert UTC datetime to local timezone (default IST)
const convertUTCToLocal = (date, time, timeZone = 'Asia/Kolkata') => {
  if (!date) return '-';

  try {
    // Combine date and time if time is provided
    const dateTimeString = time ? `${date} ${time}` : date;

    // Parse the UTC datetime
    const utcDate = new Date(dateTimeString + (time ? ' UTC' : ''));

    if (isNaN(utcDate.getTime())) {
      return dateTimeString; // Return original if parsing fails
    }

    // Convert to local timezone
    const options = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone: timeZone
    };

    return new Intl.DateTimeFormat('en-US', options).format(utcDate);
  } catch (error) {
    console.error('Error converting timezone:', error);
    return time ? `${date} ${time}` : date;
  }
};

// Logger utility - logs to console only (no UI messages)
const logger = {
  debug: (message, data = {}) => {
    console.log(`[ATTENDANCE_DEBUG] ${message}`, data);
  },
  error: (message, error = {}) => {
    console.error(`[ATTENDANCE_ERROR] ${message}`, error);
  },
  info: (message, data = {}) => {
    console.info(`[ATTENDANCE_INFO] ${message}`, data);
  },
};

const AttendanceClients = () => {
  const location = useLocation();
  const openMenuId = location.state;
  const driverImage = config.apiImageUrl;
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  // State management
  const [monthValue, setMonthValue] = useState(currentMonth);
  const [yearValue, setYearValue] = useState(currentYear);
  const [openAttendance, setOpenAttendance] = useState(false);
  const [attendanceDates, setAttendanceDates] = useState(null);
  const [activeIcon, setActiveIcon] = useState("list");
  const [showTable, setShowTable] = useState(true);
  const [showDays, setShowDays] = useState(true);
  const [showGrid, setShowGrid] = useState(false);
  const [workingDays, setWorkingDays] = useState(31);
  const [dates, setDates] = useState([]);
  const [employeeData, setEmployeeData] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailValue, setDetailValue] = useState(null);
  const [openAddAttendanceModal, setOpenAddAttendanceModal] = useState(false);
  const [pendingAddAttendance, setPendingAddAttendance] = useState(null);
  const [addAttendanceAddress, setAddAttendanceAddress] = useState("");
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteShowToUser, setDeleteShowToUser] = useState("0");
  const [deleteError, setDeleteError] = useState("");

  const finalTime = `${yearValue}-${String(monthValue + 1).padStart(2, "0")}`;

  // Get client details
  useEffect(() => {
    getData();
  }, [openMenuId]);

  const getData = async () => {
    try {
      setLoading(true);
      const rowData = await clientApis.clientViewMoreId(openMenuId);
      setDetailValue(rowData);
      logger.info("Client data fetched successfully", { clientId: openMenuId });
    } catch (err) {
      logger.error("Error fetching client data", {
        clientId: openMenuId,
        message: err.message,
        status: err.response?.status,
      });
    } finally {
      setLoading(false);
    }
  };

  const { client } = detailValue || {};
  const { user_id } = client || {};

  // Fetch employee attendance data when user_id or date changes
  useEffect(() => {
    if (user_id) {
      logger.debug("Triggering attendance data fetch", {
        user_id,
        monthValue,
        yearValue,
        finalTime,
      });
      getEmployeeData();
    }
  }, [monthValue, yearValue, user_id]);

  const getEmployeeData = async () => {
    try {
      setLoading(true);

      if (!user_id) {
        logger.error("Missing user_id", { user_id });
        setLoading(false);
        return;
      }

      if (!finalTime || finalTime.length !== 7) {
        logger.error("Invalid finalTime format", {
          finalTime,
          monthValue,
          yearValue,
        });
        setLoading(false);
        return;
      }

      logger.info("Making API request", {
        user_id,
        finalTime,
        timestamp: new Date().toISOString(),
      });

      const result = await clientApis.clientDriverAttendance(user_id, finalTime);

      logger.info("API response received", {
        recordCount: result?.length || 0,
        hasData: Array.isArray(result) && result.length > 0,
      });

      if (result && Array.isArray(result) && result.length > 0) {
        setEmployeeData(result);
        setFilteredEmployees(result);
        logger.debug("Employee data set successfully", {
          count: result.length,
        });
      } else {
        setEmployeeData([]);
        setFilteredEmployees([]);
        logger.info("No attendance data found", {
          user_id,
          finalTime,
        });
      }
    } catch (err) {
      logger.error("Error fetching attendance data", {
        user_id,
        finalTime,
        statusCode: err.response?.status,
        message: err.message,
        responseData: err.response?.data,
        config: {
          url: err.config?.url,
          method: err.config?.method,
        },
      });

      setEmployeeData([]);
      setFilteredEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  // Calculate days in selected month
  useEffect(() => {
    const daysInMonth = getDaysInMonth(monthValue, yearValue);
    setDates(Array.from({ length: daysInMonth }, (_, i) => i + 1));
    setWorkingDays(daysInMonth);
  }, [monthValue, yearValue]);

  // Filter employees based on selection
  useEffect(() => {
    if (selectedEmployee) {
      const filtered = employeeData.filter(
        (emp) => emp.driver_id === selectedEmployee
      );
      setFilteredEmployees(filtered);
      logger.debug("Filtered employees", {
        selectedEmployee,
        count: filtered.length,
      });
    } else {
      setFilteredEmployees(employeeData);
    }
  }, [selectedEmployee, employeeData]);

  // Get attendance record for specific date
  const getAttendanceForDate = (attendanceList, day) => {
    const dateStr = formatDateString(monthValue, yearValue, day);
    return attendanceList.find((a) => a.date === dateStr) || null;
  };

  // Handlers
  const handleStatusClick = (attendance, employeeId, day, isClickableForAdd = false) => {
    if (attendance) {
      if (isClickableForAdd) {
        // Open add attendance confirmation modal for non-present records
        const dateStr = formatDateString(monthValue, yearValue, day);
        setPendingAddAttendance({
          employeeId,
          date: dateStr,
          day,
        });
        setOpenAddAttendanceModal(true);
        logger.debug("Add attendance confirmation opened", {
          employeeId,
          date: dateStr,
        });
      } else if (attendance?.pic) {
        // View existing attendance details
        setAttendanceDates(attendance);
        setOpenAttendance(true);
        logger.debug("Attendance details opened", {
          date: attendance.date,
          hasPic: !!attendance.pic,
        });
      }
    }
  };

  const handleCloseAttendance = () => {
    setOpenAttendance(false);
  };

  const handleCloseAddAttendanceModal = () => {
    setOpenAddAttendanceModal(false);
    setPendingAddAttendance(null);
    setAddAttendanceAddress("");
  };

  const handleConfirmAddAttendance = async () => {
    if (!pendingAddAttendance) return;

    try {
      // Use the clicked date with current system time
      const clickedDate = pendingAddAttendance.date; // Already in format YYYY-MM-DD

      // Get current UTC time
      const now = new Date();
      const hours = String(now.getUTCHours()).padStart(2, '0');
      const minutes = String(now.getUTCMinutes()).padStart(2, '0');
      const seconds = String(now.getUTCSeconds()).padStart(2, '0');
      const currentTime = `${hours}:${minutes}:${seconds}`;

      // Combine clicked date with current UTC time: YYYY-MM-DD HH:MM:SS (UTC)
      const attendanceDateTime = `${clickedDate} ${currentTime}`;

      logger.info("Adding attendance", {
        clientId: openMenuId,
        driverId: pendingAddAttendance.employeeId,
        date: attendanceDateTime,
        address: addAttendanceAddress,
      });

      // Call API to add attendance
      const formData = new FormData();
      formData.append("client_id", openMenuId);
      formData.append("driver_id", pendingAddAttendance.employeeId);
      formData.append("date", attendanceDateTime);
      if (addAttendanceAddress) {
        formData.append("address", addAttendanceAddress);
      }

      // Debug: Log the form data
      console.log("Form Data being sent:");
      for (let [key, value] of formData.entries()) {
        console.log(`${key}:`, value);
      }

      const response = await clientApis.addDriverAttendance(formData);

      logger.info("Attendance added successfully", {
        clientId: openMenuId,
        driverId: pendingAddAttendance.employeeId,
        date: attendanceDateTime,
        address: addAttendanceAddress,
        response: response,
      });

      // Show success message from API if available
      if (response?.message) {
        alert(response.message);
      } else if (response?.status === true) {
        alert("Attendance added successfully!");
      }

      // Refresh data
      await getEmployeeData();
    } catch (error) {
      logger.error("Error adding attendance", {
        error: error.message,
        clientId: openMenuId,
        driverId: pendingAddAttendance.employeeId,
        date: pendingAddAttendance.date,
        response: error.response?.data,
      });

      // Use API error message if available, otherwise use generic message
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to add attendance. Please try again.";

      alert(errorMessage);
    } finally {
      handleCloseAddAttendanceModal();
    }
  };

  const handleDeleteClick = () => {
    setOpenDeleteModal(true);
    setDeleteReason("");
    setDeleteShowToUser("0");
    setDeleteError("");
  };

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
    setDeleteReason("");
    setDeleteShowToUser("0");
    setDeleteError("");
  };

  const handleConfirmDelete = async () => {
    if (!deleteReason.trim()) {
      setDeleteError("Please enter a reason (required)");
      return;
    }

    if (deleteReason.length > 500) {
      setDeleteError("Reason must be 500 characters or less");
      return;
    }

    try {
      // Get the attendance ID from the attendance record (from API response)
      const attendanceId = attendanceDates?.id;

      if (!attendanceId) {
        setDeleteError("Unable to find attendance record ID");
        return;
      }

      logger.info("Deleting attendance", {
        attendanceId: attendanceId,
        reason: deleteReason,
        status: deleteShowToUser,
        fullAttendanceRecord: attendanceDates,
      });

      // Call API to delete attendance
      const formData = new FormData();
      formData.append("attendance_id", attendanceId);  // Use the ID from attendance record
      formData.append("status", parseInt(deleteShowToUser));
      formData.append("reason", deleteReason);

      // Debug: Log the form data
      console.log("Delete Form Data being sent:");
      console.log("Attendance Record:", attendanceDates);
      console.log("Attendance ID:", attendanceId);
      for (let [key, value] of formData.entries()) {
        console.log(`${key}:`, value);
      }

      const response = await clientApis.deleteAttendanceRecord(formData);

      logger.info("Attendance deleted successfully", {
        attendanceId: attendanceId,
      });

      // Close modals and refresh data
      setOpenAttendance(false);
      handleCloseDeleteModal();
      await getEmployeeData();
    } catch (error) {
      logger.error("Error deleting attendance", {
        error: error.message,
        attendanceId: attendanceDates?.id,
        response: error.response?.data,
      });

      // Use API error message if available, otherwise use generic message
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to delete attendance. Please try again.";

      setDeleteError(errorMessage);
    }
  };

  const handleListIconClick = () => {
    setActiveIcon("list");
    setShowTable(true);
    setShowGrid(false);
    setShowDays(true);
  };

  const handlePersonIconClick = () => {
    setActiveIcon("person");
    setShowGrid(true);
    setShowTable(false);
  };

  const handleExportClick = () => {
    try {
      logger.info("Starting export process", {
        employeeCount: filteredEmployees.length,
        dateCount: dates.length,
      });

      const wb = XLSX.utils.book_new();
      const exportData = [];

      exportData.push({
        "Employee Name": "Attendance Report",
        "": `${months[monthValue]} ${yearValue}`,
      });
      exportData.push({});

      const headerRow = { "Employee Name": "Employee Name" };
      dates.forEach((date) => {
        headerRow[`${date}`] = date;
      });
      headerRow["Total Present"] = "Total Present";
      exportData.push(headerRow);

      filteredEmployees.forEach((employee) => {
        const stats = calculateAttendanceStats(employee);
        const dataRow = { "Employee Name": employee.username };
        dates.forEach((date) => {
          const attendance = getAttendanceForDate(employee.attendance, date);
          if (attendance) {
            if (attendance.status === "1") {
              dataRow[`${date}`] = "✓";
            } else if (attendance.status === "1603") {
              dataRow[`${date}`] = "✕";
            } else {
              dataRow[`${date}`] = statusConfig[attendance.status]?.label || attendance.status;
            }
          } else {
            dataRow[`${date}`] = "";
          }
        });
        dataRow["Total Present"] = stats.presentCount;
        exportData.push(dataRow);
      });

      const ws = XLSX.utils.json_to_sheet(exportData);
      const colWidths = [25, ...dates.map(() => 12), 15];
      ws["!cols"] = colWidths.map((width) => ({ wch: width }));

      const range = XLSX.utils.decode_range(ws["!ref"]);
      for (let row = range.s.r; row <= range.e.r; row++) {
        for (let col = range.s.c; col <= range.e.c; col++) {
          const cellAddress = XLSX.utils.encode_col(col) + XLSX.utils.encode_row(row);
          if (!ws[cellAddress]) continue;
          const cell = ws[cellAddress];
          const cellValue = cell.v;

          if (row === 2) {
            cell.s = {
              font: { bold: true, color: { rgb: "FFFFFF" } },
              fill: { fgColor: { rgb: "1976d2" } },
              alignment: { horizontal: "center", vertical: "center" },
            };
          }

          if (row === 0) {
            cell.s = {
              font: { bold: true, size: 14, color: { rgb: "000000" } },
              alignment: { horizontal: "left" },
            };
          }

          if (cellValue === "✓") {
            cell.s = {
              font: { color: { rgb: "00B050" }, bold: true, size: 14 },
              alignment: { horizontal: "center", vertical: "center" },
            };
          } else if (cellValue === "✕") {
            cell.s = {
              font: { color: { rgb: "FF0000" }, bold: true, size: 14 },
              alignment: { horizontal: "center", vertical: "center" },
            };
          } else if (row > 2 && col > 0 && col < dates.length + 1) {
            cell.s = { alignment: { horizontal: "center", vertical: "center" } };
          }
        }
      }

      XLSX.utils.book_append_sheet(wb, ws, "Attendance");
      const fileName = `Attendance_${months[monthValue]}_${yearValue}.xlsx`;
      XLSX.writeFile(wb, fileName);

      logger.info("Export completed successfully", {
        fileName,
        recordCount: filteredEmployees.length,
      });
    } catch (error) {
      logger.error("Error exporting data", {
        message: error.message,
        stack: error.stack,
      });
    }
  };

  const handleEmployeeChange = (event) => {
    setSelectedEmployee(event.target.value);
  };

  const handleMonthChange = (event) => {
    const newMonth = parseInt(event.target.value);
    setMonthValue(newMonth);
    logger.debug("Month changed", { newMonth, year: yearValue });
  };

  const handleYearChange = (event) => {
    const newYear = parseInt(event.target.value);
    setYearValue(newYear);
    logger.debug("Year changed", { month: monthValue, newYear });
  };

  // Styles
  const buttonStyle = {
    padding: "8px 16px",
    backgroundColor: "#1d82f5",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
  };

  const iconButtonStyle = (isActive) => ({
    padding: "8px 12px",
    backgroundColor: isActive ? "orange" : "#333",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "18px",
    transition: "background-color 0.3s",
  });

  const selectStyle = {
    padding: "8px 12px",
    backgroundColor: "#fff",
    color: "#000",
    border: "1px solid #ccc",
    borderRadius: "4px",
    fontSize: "14px",
    cursor: "pointer",
  };

  // Calculate attendance statistics for an employee
  const calculateAttendanceStats = (employee) => {
    const presentCount = employee.attendance.filter((a) => a.status === "1").length;
    const absentCount = employee.attendance.filter((a) => a.status === "1603").length;
    const leaveCount = employee.attendance.filter((a) => a.status === "1612").length;
    const relievedCount = employee.attendance.filter((a) => a.status === "1605").length;
    return { presentCount, absentCount, leaveCount, relievedCount };
  };

  // Calculate total statistics
  const calculateTotalStats = () => {
    let totalPresent = 0, totalAbsent = 0, totalLeave = 0, totalRelieved = 0;
    filteredEmployees.forEach((emp) => {
      const stats = calculateAttendanceStats(emp);
      totalPresent += stats.presentCount;
      totalAbsent += stats.absentCount;
      totalLeave += stats.leaveCount;
      totalRelieved += stats.relievedCount;
    });
    return { totalPresent, totalAbsent, totalLeave, totalRelieved };
  };

  // Render attendance icon
  const renderAttendanceIcon = (attendance, employeeId = null, day = null) => {
    if (!attendance) return null;

    // Check if present record has pic = "1" (keyboard icon logic)
    const hasPicOne = attendance.status === "1" && attendance.pic === "1";

    // Get status configuration
    let statusItem = statusConfig[attendance.status];

    // Use keyboard icon for present records with pic = "1"
    if (hasPicOne) {
      statusItem = statusConfig["1_no_loc"];
    }

    if (!statusItem) return null;

    // Make all non-present records clickable for adding attendance
    const isClickableForAdd = attendance.status !== "1";

    return (
      <span
        onClick={() => handleStatusClick(attendance, employeeId, day, isClickableForAdd)}
        style={{
          fontSize: "20px",
          color: statusItem.color,
          cursor: attendance.pic || isClickableForAdd ? "pointer" : "default",
          fontWeight: "bold",
        }}
        title={statusItem.label}
      >
        {statusItem.icon}
      </span>
    );
  };

  const totalStats = calculateTotalStats();

  return (
    <div style={{ padding: "20px", fontFamily: "Arial, sans-serif" }}>
      {/* Header */}
      <div style={{ display: "flex", gap: "10px", marginBottom: "20px", flexWrap: "wrap" }}>
        <button onClick={handleExportClick} style={buttonStyle}>
          📥 Export
        </button>
        <select value={monthValue} onChange={handleMonthChange} style={selectStyle}>
          {months.map((month, idx) => (
            <option key={idx} value={idx}>{month}</option>
          ))}
        </select>
        <select value={yearValue} onChange={handleYearChange} style={selectStyle}>
          {[2023, 2024, 2025, 2026].map((year) => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
        <div style={{ display: "flex", gap: "5px" }}>
          <button onClick={handleListIconClick} style={iconButtonStyle(activeIcon === "list")}>≡</button>
          <button onClick={handlePersonIconClick} style={iconButtonStyle(activeIcon === "person")}>👤</button>
        </div>
      </div>

      {/* Loading */}
      {loading && employeeData.length === 0 && (
        <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>⏳ Loading...</div>
      )}

      {/* Table View */}
      {showTable && !loading && (
        <div style={{ overflowX: "auto", marginBottom: "30px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", backgroundColor: "white", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
            <tbody>
              <tr style={{ backgroundColor: "#f5f5f5" }}>
                <td colSpan={dates.length + 2} style={{ padding: "10px" }}>
                  <strong style={{ color: "#000" }}>Legend:</strong> {Object.entries(statusConfig).map(([key, c]) => (
                    <span key={key} style={{ marginRight: "20px", color: "#000" }}>{c.icon} {c.label}</span>
                  ))}
                </td>
              </tr>
              <tr style={{ backgroundColor: "#1976d2", color: "white" }}>
                <th style={{ padding: "10px", textAlign: "left" }}>Employee</th>
                {dates.map((date) => (
                  <th key={date} style={{ padding: "10px", textAlign: "center", minWidth: "50px" }}>{date}</th>
                ))}
                <th style={{ padding: "10px" }}>Total Present</th>
              </tr>
              {showDays && (
                <tr style={{ backgroundColor: "#e3f2fd" }}>
                  <td style={{ padding: "10px" }}></td>
                  {dates.map((date) => (
                    <td key={date} style={{ padding: "10px", textAlign: "center", fontSize: "12px", color: "#666" }}>
                      {getDayName(date, monthValue, yearValue)}
                    </td>
                  ))}
                  <td></td>
                </tr>
              )}
              {filteredEmployees.length > 0 ? (
                filteredEmployees.map((employee, idx) => {
                  const stats = calculateAttendanceStats(employee);
                  return (
                    <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? "white" : "#f9f9f9", borderBottom: "1px solid #ddd" }}>
                      <td style={{ padding: "10px", fontWeight: "500", color: "#000" }}>
                        {employee.username}<br /><span style={{ fontSize: "12px", color: "#000" }}>{employee.driver_id}</span>
                      </td>
                      {dates.map((date) => {
                        const attendance = getAttendanceForDate(employee.attendance, date);
                        // Check if this is a present record with pic = "1"
                        const hasPicOne =
                          attendance &&
                          attendance.status === "1" &&
                          attendance.pic === "1";

                        return (
                          <td
                            key={date}
                            style={{
                              padding: "10px",
                              textAlign: "center",
                              backgroundColor: hasPicOne
                                ? statusConfig["1_no_loc"]?.bgColor
                                : attendance
                                ? statusConfig[attendance.status]?.bgColor
                                : "transparent",
                            }}
                          >
                            {renderAttendanceIcon(attendance, employee.driver_id, date)}
                          </td>
                        );
                      })}
                      <td style={{ padding: "10px", textAlign: "center" }}><strong style={{ color: "#000" }}>{stats.presentCount}</strong></td>
                    </tr>
                  );
                })
              ) : (
                <tr><td colSpan={dates.length + 2} style={{ padding: "30px", textAlign: "center", color: "#999" }}>📭 No data</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Grid View */}
      {showGrid && !loading && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "15px", marginBottom: "30px" }}>
            {[
              { title: "Working Days", value: workingDays },
              { title: "Total Present", value: totalStats.totalPresent },
              { title: "On Leave", value: totalStats.totalLeave },
              { title: "Absent", value: totalStats.totalAbsent },
            ].map((stat, idx) => (
              <div key={idx} style={{ backgroundColor: "white", padding: "20px", borderRadius: "8px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)", textAlign: "center" }}>
                <div style={{ fontSize: "14px", color: "#666", marginBottom: "10px" }}>{stat.title}</div>
                <div style={{ fontSize: "32px", fontWeight: "bold", color: "#1976d2" }}>{stat.value}</div>
              </div>
            ))}
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label style={{ marginRight: "10px", fontWeight: "500" }}>👤 Filter by Driver:</label>
            <select value={selectedEmployee} onChange={handleEmployeeChange} style={selectStyle}>
              <option value="">All Drivers</option>
              {employeeData.map((emp) => (
                <option key={emp.driver_id} value={emp.driver_id}>{emp.username}</option>
              ))}
            </select>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", backgroundColor: "white", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
              <thead>
                <tr style={{ backgroundColor: "#1976d2", color: "white" }}>
                  <th style={{ padding: "12px", textAlign: "left" }}>Date & Time</th>
                  <th style={{ padding: "12px", textAlign: "left" }}>Driver</th>
                  <th style={{ padding: "12px", textAlign: "center" }}>Status</th>
                  <th style={{ padding: "12px", textAlign: "left" }}>Address</th>
                  <th style={{ padding: "12px", textAlign: "left" }}>Coordinates</th>
                  <th style={{ padding: "12px", textAlign: "center" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.length > 0 ? (
                  filteredEmployees.flatMap((emp) =>
                    emp.attendance.sort((a, b) => new Date(b.date) - new Date(a.date)).map((att, idx) => {
                      // Check if this is a present record with pic = "1"
                      const hasPicOne = att.status === "1" && att.pic === "1";

                      const statusItem = hasPicOne
                        ? statusConfig["1_no_loc"]
                        : statusConfig[att.status];

                      return (
                        <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? "white" : "#f9f9f9", borderBottom: "1px solid #ddd" }}>
                          <td style={{ padding: "12px" }}>{convertUTCToLocal(att.date, att.time)}</td>
                          <td style={{ padding: "12px" }}>{emp.username}</td>
                          <td
                            style={{
                              padding: "12px",
                              textAlign: "center",
                              backgroundColor: hasPicOne
                                ? statusConfig["1_no_loc"]?.bgColor
                                : "transparent",
                            }}
                          >
                            {renderAttendanceIcon(att)}
                          </td>
                          <td style={{ padding: "12px" }}>{att.address || "-"}</td>
                          <td style={{ padding: "12px" }}>
                            {att.lat && att.lng && att.lat !== "0" && att.lng !== "0"
                              ? `${att.lat.substring(0, 8)}, ${att.lng.substring(0, 8)}`
                              : "-"}
                          </td>
                          <td style={{ padding: "12px", textAlign: "center" }}>
                            {att.pic && att.pic !== "1" && (
                              <button onClick={() => handleStatusClick(att)} style={{ padding: "6px 12px", backgroundColor: "#1976d2", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "12px" }}>View</button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )
                ) : (
                  <tr><td colSpan="6" style={{ padding: "30px", textAlign: "center", color: "#999" }}>📭 No records</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal */}
      {openAttendance && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 }} onClick={handleCloseAttendance}>
          <div style={{ backgroundColor: "white", padding: "30px", borderRadius: "8px", maxWidth: "500px", boxShadow: "0 4px 20px rgba(0,0,0,0.3)" }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginTop: 0, color: "#000" }}>Attendance Details</h2>
            {attendanceDates && (
              <div>
                <p style={{ color: "#000" }}><strong>Date & Time:</strong> {convertUTCToLocal(attendanceDates.date, attendanceDates.time)}</p>
                <p style={{ color: "#000" }}><strong>Status:</strong> {statusConfig[attendanceDates.status]?.label}</p>
                <p style={{ color: "#000" }}><strong>Location:</strong> {attendanceDates.address || "N/A"}</p>
                <p style={{ color: "#000" }}><strong>Coordinates:</strong> Lat: {attendanceDates.lat || "N/A"}, Lng: {attendanceDates.lng || "N/A"}</p>
                {attendanceDates.pic && (
                  <div>
                    <p style={{ color: "#000" }}><strong>Photo:</strong></p>
                    <img src={`${driverImage}${attendanceDates.pic}`} alt="Attendance" style={{ maxWidth: "100%", maxHeight: "300px", borderRadius: "4px" }} onError={(e) => { e.target.src = "https://via.placeholder.com/300x300?text=Not+Found"; e.target.style.border = "1px solid #f44336"; }} />
                  </div>
                )}
              </div>
            )}
            <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
              <button onClick={handleDeleteClick} style={{ ...buttonStyle, flex: 1, backgroundColor: "#f44336" }}>Delete</button>
              <button onClick={handleCloseAttendance} style={{ ...buttonStyle, flex: 1, backgroundColor: "#333" }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Attendance Confirmation Modal */}
      {openAddAttendanceModal && pendingAddAttendance && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 }} onClick={handleCloseAddAttendanceModal}>
          <div style={{ backgroundColor: "white", padding: "30px", borderRadius: "8px", maxWidth: "400px", boxShadow: "0 4px 20px rgba(0,0,0,0.3)" }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginTop: 0, color: "#1976d2" }}>Add Attendance</h2>
            <p style={{ fontSize: "16px", lineHeight: "1.5", color: "#000" }}>
              Do you want to add attendance for <strong>{pendingAddAttendance.date}</strong>?
            </p>
            <div style={{ marginTop: "20px" }}>
              <label style={{ display: "block", marginBottom: "8px", fontWeight: "500", color: "#000" }}>
                Address (Optional)
              </label>
              <textarea
                value={addAttendanceAddress}
                onChange={(e) => setAddAttendanceAddress(e.target.value)}
                placeholder="Enter address (optional)..."
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  fontSize: "14px",
                  fontFamily: "Arial, sans-serif",
                  minHeight: "80px",
                  resize: "vertical",
                  backgroundColor: "#fff",
                  color: "#000",
                }}
              />
            </div>
            <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
              <button
                onClick={handleConfirmAddAttendance}
                style={{
                  ...buttonStyle,
                  flex: 1,
                  backgroundColor: "#4caf50",
                }}
              >
                Yes
              </button>
              <button
                onClick={handleCloseAddAttendanceModal}
                style={{
                  ...buttonStyle,
                  flex: 1,
                  backgroundColor: "#f44336",
                }}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Attendance Modal */}
      {openDeleteModal && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 }} onClick={handleCloseDeleteModal}>
          <div style={{ backgroundColor: "white", padding: "30px", borderRadius: "8px", maxWidth: "500px", boxShadow: "0 4px 20px rgba(0,0,0,0.3)" }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginTop: 0, color: "#f44336" }}>Delete Attendance</h2>
            <p style={{ fontSize: "16px", lineHeight: "1.5", color: "#000" }}>
              Are you sure you want to delete this attendance record?
            </p>
            <div style={{ marginTop: "20px" }}>
              <label style={{ display: "block", marginBottom: "8px", fontWeight: "500", color: "#000" }}>
                Reason <span style={{ color: "#f44336" }}>*</span>
              </label>
              <textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder="Enter reason for deletion..."
                maxLength={500}
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  fontSize: "14px",
                  fontFamily: "Arial, sans-serif",
                  minHeight: "100px",
                  resize: "vertical",
                  borderColor: deleteError ? "#f44336" : "#ccc",
                  color: "#000",
                  backgroundColor: "#fff",
                }}
              />
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: "5px", fontSize: "12px", color: "#666" }}>
                <span style={{ color: "#f44336" }}>{deleteError}</span>
                <span>{deleteReason.length} / 500</span>
              </div>
            </div>
            <div style={{ marginTop: "20px" }}>
              <label style={{ display: "block", marginBottom: "8px", fontWeight: "500", color: "#000" }}>
                Show to User
              </label>
              <select
                value={deleteShowToUser}
                onChange={(e) => setDeleteShowToUser(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  fontSize: "14px",
                  backgroundColor: "#fff",
                  color: "#000",
                }}
              >
                <option value="0">Do not show to user</option>
                <option value="1">Show to user</option>
              </select>
            </div>
            <div style={{ display: "flex", gap: "10px", marginTop: "25px" }}>
              <button
                onClick={handleConfirmDelete}
                style={{
                  ...buttonStyle,
                  flex: 1,
                  backgroundColor: "#f44336",
                }}
              >
                Delete
              </button>
              <button
                onClick={handleCloseDeleteModal}
                style={{
                  ...buttonStyle,
                  flex: 1,
                  backgroundColor: "#333",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceClients;